import { LitElement, PropertyValues, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { repeat } from 'lit/directives/repeat.js';
import { consume } from '@lit/context';

import { nsSharedStyles, nsLayoutStyles } from '../ns-styles/index.js';

import { LtnRouter, Route } from '../ltn-router/ltn-router.js';
import { ltnRouterContext, ltnRouteContext } from '../ns-services/contexts.js';
import { ContextChangeController } from '../ns-services/context-change.js';

import { viewPath } from '../helpers/route.js';

import { menuItem } from '../ns-services/ns-directory-service.js';

import '../ns-icon/ns-icon.js';

@customElement('ns-side-bar')
export class NSSideBar extends LitElement {
  @consume({ context: ltnRouterContext })
  private __router?: LtnRouter;

  private __routeChanges = new ContextChangeController(this, ltnRouteContext, (route: Route) =>
    this.__routeChange(route),
  );

  @property({ type: Boolean })
  fixed: boolean = false;

  @property()
  logo: string = '';

  @property({ type: Array })
  topMenu?: menuItem[];

  @property({ type: Array })
  bottomMenu?: menuItem[];

  @property({ type: String })
  activeItemRoute?: string;

  @property({ type: Array })
  activeItems?: string[];

  static styles = [
    nsLayoutStyles,
    nsSharedStyles,
    css`
      button {
        font-size: var(--wa-font-size-s);
        font-weight: 500;
      }

      :host([fixed]) {
        position: fixed;
        width: var(--ns-side-bar-width, 75px);
      }

      #side-app-bar {
        display: inline-block;

        background-color: var(--wa-color-brand-fill-loud, var(--ns-theme-purple));
        color: var(--wa-color-brand-on-loud, var(--ns-theme-black));

        border-radius: var(--wa-border-radius-l);

        height: calc(100vh - 80px);
        width: 100%;

        text-align: center;

        padding: var(--wa-space-m) 0;
      }

      #side-app-bar .logo {
        width: 50%;
      }

      #side-app-bar ul {
        list-style-type: none;

        padding: var(--wa-space-xs) 0;

        border: 1px solid var(--wa-color-brand-on-loud, var(--ns-theme-black));
        border-radius: var(--wa-border-radius-l);

        margin: var(--wa-space-m) 0 0;
      }

      #side-app-bar ul li {
        position: relative;

        padding: 0 var(--wa-space-xs);
      }

      #side-app-bar ul li ns-icon {
        --icon-height: 25px;
        --icon-width: 25px;

        color: var(--ns-theme-grey-mid);

        width: 25px;
      }
      #side-app-bar ul li ns-icon.active {
        color: var(--ns-theme-white);
      }

      #side-app-bar ul li:not(:last-child) {
        margin-bottom: var(--wa-space-m);
      }

      .badge {
        position: absolute;
        top: -4px;
        right: 2px;
        min-width: 16px;
        height: 16px;
        padding: 0 var(--wa-space-3xs);
        border-radius: var(--wa-border-radius-pill);
        background: var(--ns-theme-red);
        color: var(--ns-theme-black);
        font-size: var(--wa-font-size-3xs);
        line-height: 16px;
        text-align: center;
        pointer-events: none;
      }

      button {
        background: transparent;
        padding: 0;
        border: 0;
      }
    `,
  ];

  async connectedCallback() {
    super.connectedCallback();

    if (this.__router) {
      // Grab the initial route
      this.__routeChange(this.__router.route);
    }

    await this.updateComplete;
  }

  private __routeChange(route: Route) {
    // We'll check the top and bottom menu without matching on /
    if (route.path !== '/') {
      // Check top menu
      let item = this.topMenu?.find((i) => i.link !== '/' && route.path.indexOf(i.link) === 0);
      if (item) return (this.activeItemRoute = item.id);

      // Check bottom menu
      item = this.bottomMenu?.find((i) => i.link !== '/' && route.path.indexOf(i.link) === 0);
      if (item) return (this.activeItemRoute = item.id);
    } else {
      const [firstItem] = this.topMenu ? this.topMenu : [];
      this.activeItemRoute = this.topMenu?.find((i) => route.path.indexOf(i.link) === 0)?.id;
      return (this.activeItemRoute = this.activeItemRoute === undefined ? firstItem.id : this.activeItemRoute);
    }

    // We haven't matched by now so unset
    this.activeItemRoute = undefined;
  }

  private __isActive(item: menuItem): boolean {
    if (this.activeItemRoute === item.id) return true;

    if (!this.activeItems) return false;
    if (this.activeItems.indexOf(item.id) !== -1) return true;

    return false;
  }

  private __renderMenu(items?: menuItem[]) {
    if (!items) return undefined;

    return repeat(
      items,
      (item) => item,
      (item) => html`
        <li>
          <button
            class="clickable"
            aria-label="${item.title}${item.badgeCount ? ` (${item.badgeCount} unread)` : ''}"
            data-title="${item.title}"
            aria-current="${this.__isActive(item) ? 'page' : 'false'}"
            @click="${() => (item.fn ? item.fn() : viewPath(item.link))}"
          >
            <ns-icon
              class="${this.__isActive(item) ? 'active' : ''} ${item.id}"
              ?bold="${this.__isActive(item)}"
              icon="${item.icon}"
              aria-hidden="true"
            ></ns-icon>
          </button>
          ${item.badgeCount
            ? html`
                <div class="badge" data-badge-for=${item.id}>${item.badgeCount > 99 ? '99+' : item.badgeCount}</div>
              `
            : ''}
        </li>
      `,
    );
  }

  // The menu items are rebuilt on every app render, so a CSS animation on the badge would replay each time
  private __badgeCounts = new Map<string, number | undefined>();

  // An item added after the route loaded still shows as the current page
  protected override willUpdate(changed: PropertyValues<this>) {
    if (changed.has('topMenu') && this.__router?.route) this.__routeChange(this.__router.route);
  }

  protected override updated() {
    for (const item of [...(this.topMenu ?? []), ...(this.bottomMenu ?? [])]) {
      const previous = this.__badgeCounts.get(item.id);
      if (item.badgeCount !== undefined && previous !== undefined && item.badgeCount > previous) {
        this.renderRoot
          .querySelector(`[data-badge-for="${item.id}"]`)
          ?.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.3)' }, { transform: 'scale(1)' }], {
            duration: 300,
            easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
          });
      }
      this.__badgeCounts.set(item.id, item.badgeCount);
    }
  }

  protected override render() {
    return html`
      <div id="side-app-bar" role="navigation" aria-label="Main menu" data-tour-id="side-bar">
        <div class="h-100 layout vertical">
          <div class="layout vertical center flex">
            ${when(
              this.logo,
              () => html`
                <img class="logo" alt="" aria-hidden="true" src="${this.logo}" />
              `,
            )}
            <ul class="top-menu">
              ${this.__renderMenu(this.topMenu)}
            </ul>
          </div>
          <div class="layout vertical center">
            <ul class="bottom-menu">
              ${this.__renderMenu(this.bottomMenu)}
            </ul>
          </div>
        </div>
      </div>
    `;
  }
}
